import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, DollarSign, Mail, Phone } from "lucide-react";
import { api } from "../../../lib/api";

const STAGES = [
  { key: "new_lead",         label: "New Lead",         accent: "text-cyan"       },
  { key: "contacted",        label: "Contacted",        accent: "text-cyan"       },
  { key: "quote_sent",       label: "Quote Sent",       accent: "text-amber-300"  },
  { key: "follow_up_needed", label: "Follow-Up",        accent: "text-amber-300"  },
  { key: "deposit_pending",  label: "Deposit Pending",  accent: "text-amber-300"  },
  { key: "booked",           label: "Booked",           accent: "text-lime-300"   },
  { key: "in_progress",      label: "In Progress",      accent: "text-lime-300"   },
  { key: "completed",        label: "Completed",        accent: "text-white/70"   },
  { key: "canceled",         label: "Canceled",         accent: "text-red-300"    },
  { key: "lost_lead",        label: "Lost",             accent: "text-red-300"    },
  { key: "repeat_customer",  label: "Repeat",           accent: "text-yellow-200" },
  { key: "vip_customer",     label: "VIP",              accent: "text-yellow-200" },
];

/**
 * CRMPipeline — kanban board of approved leads across 12 stages.
 * Drag a card between columns to update its stage. Powered by
 * PATCH /admin/crm/leads/:id { stage }.
 */
export default function CRMPipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/crm/leads?status=approved")
      .then((r) => setLeads(r.data || []))
      .catch(() => toast.error("Failed to load pipeline"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const byStage = useMemo(() => {
    const map = {};
    STAGES.forEach((s) => { map[s.key] = []; });
    leads.forEach((l) => {
      const s = l.stage || "new_lead";
      (map[s] = map[s] || []).push(l);
    });
    return map;
  }, [leads]);

  const drop = async (stage) => {
    if (!dragging || dragging.stage === stage) { setDragging(null); return; }
    // optimistic
    const prev = leads;
    setLeads(leads.map((l) => l.id === dragging.id ? { ...l, stage } : l));
    setDragging(null);
    setHoverStage(null);
    try {
      await api.patch(`/admin/crm/leads/${dragging.id}`, { stage });
      toast.success(`Moved to ${STAGES.find((s) => s.key === stage)?.label}`);
    } catch {
      toast.error("Move failed");
      setLeads(prev);
    }
  };

  return (
    <div data-testid="crm-pipeline" className="space-y-3">
      <div className="text-white/50 text-xs label-caps">
        {loading ? "loading…" : `${leads.length} lead${leads.length === 1 ? "" : "s"} in pipeline · drag between columns`}
      </div>
      <div className="overflow-x-auto -mx-1">
        <div className="flex gap-2 min-w-max px-1 pb-2">
          {STAGES.map((s) => {
            const items = byStage[s.key] || [];
            return (
              <div
                key={s.key}
                data-testid={`crm-stage-${s.key}`}
                onDragOver={(e) => { if (dragging) { e.preventDefault(); setHoverStage(s.key); } }}
                onDragLeave={() => setHoverStage((h) => (h === s.key ? null : h))}
                onDrop={(e) => { e.preventDefault(); drop(s.key); }}
                className={`w-56 shrink-0 rounded-2xl border bg-black/25 p-2.5 ${
                  hoverStage === s.key ? "border-cyan/60 bg-cyan/[0.05]" : "border-white/10"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`label-caps text-[10px] ${s.accent} tracking-widest`}>{s.label}</div>
                  <div className="text-white/40 text-[10px]">{items.length}</div>
                </div>
                <div className="space-y-1.5 max-h-[65vh] overflow-y-auto">
                  {items.map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={() => setDragging(l)}
                      onDragEnd={() => setDragging(null)}
                      data-testid={`crm-pipeline-card-${l.id}`}
                      className="rounded-xl border border-white/10 bg-ink-900 p-2.5 cursor-grab active:cursor-grabbing hover:border-cyan/40 transition"
                    >
                      <div className="text-white text-sm font-medium truncate">
                        {l.name || <span className="text-white/40 italic">Unnamed</span>}
                      </div>
                      {l.vehicle_name && <div className="text-white/50 text-[10px] mt-0.5 truncate">{l.vehicle_name}</div>}
                      <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/40">
                        <span>{l.source || "—"}</span>
                        {l.estimated_value != null && (
                          <span className="inline-flex items-center gap-0.5 text-lime-300">
                            <DollarSign size={9} />{Number(l.estimated_value).toFixed(0)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-white/25 text-[10px] italic text-center py-6">empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
